import { Controller } from "@hotwired/stimulus";

export default class extends Controller {
  static targets = ["input", "results", "blank", "numResults", "backButton", "empty", "scopeIcon", "advancedSearchLink"]
  static values = {
    structureName: String,
    structurePlural: { type: String, default: "" }
  }
  static minSearchChars = 3 // Number of characters to trigger search

  connect() {
    this.selectedIndex = -1
    this.bindKeyboardEvents()
    // Initialize search preferences
    const comprehensiveChip = this.element.querySelector('.chip--selectable[data-search-comprehensive-default-value]')
    this.searchByAll = comprehensiveChip?.dataset.searchByAll === "true"
    this.searchByExact = false
    this.onlyMuseum = false
    // Get initial company state from the active chip if it exists
    const companyChip = this.element.querySelector('.chip--selectable[data-company-id]')
    this.companyId = companyChip?.dataset.companyId
    this.onlyCompany = companyChip?.classList.contains('active') || false
    this.onlyCompanyDefault = this.onlyCompany

    // Scope: null searches all types; a plural type key searches one type
    this.specificViewMode = false
    this.specificViewType = null
    if (this.hasScopeIconTarget) {
      this.defaultScopeIcon = this.scopeIconTarget.className
    }
    this.defaultPlaceholder = this.inputTarget.placeholder

    // Initialize view mode tracking
    this.currentViewMode = 'all' // 'all' or 'single'
    this.currentViewType = null

    // Initialize debounce timer
    this.debounceTimer = null
    this.debounceDelay = 100 // milliseconds - reduced from 300ms for faster response

    // Last consolidated payload, used to point the advanced-search link at the
    // first type that actually has results.
    this._lastSearchData = null

    // Set initial state
    this.toggleBlankState(true)
    this.updateAdvancedSearchLink()
  }

  bindKeyboardEvents() {
    // Remove any existing event listener to prevent duplicates
    this.element.removeEventListener('keydown', this.handleKeydown)

    // Create the handler and store it so we can remove it later
    this.handleKeydown = (e) => {
      // Add company chip toggle shortcut (Option + G)
      if (e.altKey && (e.key.toLowerCase() === 'g' || e.key === "©")) {  // Handle both Mac and non-Mac
        e.preventDefault()
        e.stopPropagation()
        const companyChip = this.element.querySelector('.chip--selectable[data-company-id]')
        if (companyChip && companyChip.style.display !== 'none') {
          this.toggleSearchPreference({ preventDefault: () => {}, currentTarget: companyChip })
        }
      } else {
        // Handle keyboard navigation regardless of focus
        switch(e.key) {
          case 'ArrowDown':
            e.preventDefault()
            this.moveSelection(1)
            break
          case 'ArrowUp':
            e.preventDefault()
            this.moveSelection(-1)
            break
          case 'Enter':
            // Check if a chip is focused first
            const focusedElement = document.activeElement
            const scopeItem = focusedElement && focusedElement.closest('.header__search-scope .dropdown__item')
            if (focusedElement && focusedElement.closest('.header__search-scope')) {
              // Scope UI owns Enter: activate the focused menu item; the
              // dropdown controller's own keydown toggles the trigger
              if (scopeItem) {
                e.preventDefault()
                scopeItem.click()
              }
            } else if (focusedElement && focusedElement.closest('.chip--selectable')) {
              e.preventDefault()
              this.toggleSearchPreference({ preventDefault: () => {}, currentTarget: focusedElement })
            } else if (this.selectedIndex >= 0) {
              // If we have a selected result and no chip is focused, navigate to it
              e.preventDefault()
              this.selectCurrent()
            } else if (focusedElement && (focusedElement.tagName === 'A' || focusedElement.tagName === 'BUTTON')) {
              // If no result is selected and no chip is focused, try to click the focused element
              e.preventDefault()
              focusedElement.click()
            }
            break
          case 'Tab':
            // Let the default tab behavior happen
            break
        }
      }
    }

    // Add the event listener with passive option for better scroll performance
    this.element.addEventListener('keydown', this.handleKeydown, { passive: false })
  }

  moveSelection(direction) {
    const results = this.resultsTarget.querySelectorAll('.searcher__result')
    if (results.length > 0) {
      // Remove current selection
      results.forEach(result => result.classList.remove('searcher__result--selected'))

      // Update index
      this.selectedIndex += direction
      if (this.selectedIndex >= results.length) this.selectedIndex = 0
      if (this.selectedIndex < 0) this.selectedIndex = results.length - 1

      // Add selection to new item
      const selectedResult = results[this.selectedIndex]
      selectedResult.classList.add('searcher__result--selected')
      selectedResult.scrollIntoView({ block: 'nearest' })
    }
  }

  selectCurrent() {
    const results = this.resultsTarget.querySelectorAll('.searcher__result')
    const selected = results[this.selectedIndex]
    if (selected) {
      const href = selected.getAttribute('href')
      if (href) window.location.href = href
    }
  }

  toggleBlankState(show) {
    if (this.hasBlankTarget) {
      this.blankTarget.style.display = show ? 'flex' : 'none'
      this.resultsTarget.style.display = show ? 'none' : 'block'
      this.emptyTarget.style.display = 'none'
      this.numResultsTarget.style.display = show ? 'none' : 'block'
    }
  }

  setScope(event) {
    event.preventDefault()
    const { scopeType, scopeLabel, scopeIcon } = event.currentTarget.dataset

    this.specificViewMode = Boolean(scopeType)
    this.specificViewType = scopeType || null
    this.currentViewMode = 'all'
    this.currentViewType = null
    if (this.hasBackButtonTarget) this.backButtonTarget.style.display = 'none'

    if (this.hasScopeIconTarget) {
      this.scopeIconTarget.className = scopeIcon
    }
    this.inputTarget.placeholder = `Search ${scopeLabel.toLowerCase()}`
    this.updateCompanyChipVisibility()
    this.search()
    this.inputTarget.focus()
  }

  resetScope() {
    this.specificViewMode = false
    this.specificViewType = null
    if (this.hasScopeIconTarget) {
      this.scopeIconTarget.className = this.defaultScopeIcon
    }
    this.inputTarget.placeholder = this.defaultPlaceholder
    this.updateCompanyChipVisibility()
  }

  // The modal's Company-scoped view never rendered the company chip; hide it
  // client-side for the same reason: filtering companies by themselves makes
  // no sense.
  updateCompanyChipVisibility() {
    const chip = this.element.querySelector('.chip--selectable[data-company-id]')
    if (!chip) return

    chip.style.display = this.specificViewType === 'companies' ? 'none' : ''
  }

  toggleSearchPreference(event) {
    event.preventDefault()
    const chip = event.currentTarget.closest('.chip--selectable')

    // Handle company chip differently since it's exclusive
    if (chip.dataset.companyId) {
      this.onlyCompany = !this.onlyCompany
      chip.classList.toggle('active')
      this.companyId = chip.dataset.companyId
    } else {
      // Toggle other chips normally
      chip.classList.toggle('active')

      const chipText = chip.querySelector('.chip__text').textContent
      switch(chipText) {
        case 'Comprehensive':
          this.searchByAll = chip.classList.contains('active')
          break
        case 'Exact':
          this.searchByExact = chip.classList.contains('active')
          break
        case 'Museum':
          this.onlyMuseum = chip.classList.contains('active')
          break
      }
    }

    // Trigger new search with updated preferences
    this.search()
  }

  search() {
    // Clear any existing timer
    clearTimeout(this.debounceTimer)

    // Keep the advanced-search link in sync with the query and active filters
    this.updateAdvancedSearchLink()

    // Show blank state if input is empty or below min chars
    if (!this.inputTarget.value || this.inputTarget.value.length < this.constructor.minSearchChars) {
      this.resultsTarget.style.display = 'none'
      this.blankTarget.style.display = 'flex'
      this.emptyTarget.style.display = 'none'
      this.numResultsTarget.style.display = 'none'

      // If we're in single view mode and the input is empty, revert to all results
      if (this.currentViewMode === 'single' && !this.inputTarget.value) {
        this.currentViewMode = 'all'
        this.currentViewType = null
        this.backButtonTarget.style.display = 'none'
      }

      return
    }

    // Set a new timer
    this.debounceTimer = setTimeout(() => {
      this.executeSearch()
    }, this.debounceDelay)
  }

  executeSearch() {
    const query = this.inputTarget.value
    if (query.length < this.constructor.minSearchChars) {
      this.resultsTarget.style.display = 'none'
      this.blankTarget.style.display = 'flex'
      this.emptyTarget.style.display = 'none'
      this.numResultsTarget.style.display = 'none'
      if (this.hasNumResultsTarget) this.numResultsTarget.textContent = '0 results'
      return
    }

    // If there's already a fetch in progress, abort it
    if (this.currentFetch) {
      this.currentFetch.abort()
    }

    // Show loading state before fetch
    this.showLoadingState()

    const params = new URLSearchParams({
      q: query,
      search_by_all: this.searchByAll,
      search_by_exact: this.searchByExact,
      include_archived: this.onlyMuseum
    })

    // Handle specific view mode first
    if (this.specificViewMode) {
      const typeToUse = this.getSingularType(this.specificViewType)
      params.append('type', typeToUse)
    }
    // Then handle regular view modes
    else if (this.currentViewMode === 'all') {
      params.append('consolidated_results', 'true')
    } else {
      // If in single view mode, set the type parameter
      const typeToUse = this.getSingularType(this.currentViewType)
      params.append('type', typeToUse)
    }

    // Add company parameter if company filter is active; company-scoped
    // search has no chip, matching the modal's Company view
    if (this.onlyCompany && this.companyId && this.specificViewType !== 'companies') {
      params.append('company', this.companyId)
    }

    const url = `/searches?${params.toString()}`

    // Check cache first
    const cacheKey = url
    if (this._searchCache && this._searchCache.has(cacheKey)) {
      const cachedData = this._searchCache.get(cacheKey)
      this.displayResults(cachedData)
      return
    }

    // Create an AbortController for this fetch
    const controller = new AbortController()
    this.currentFetch = controller

    fetch(url, {
      headers: {
        "Accept": "application/json",
        "Cache-Control": "no-cache"
      },
      signal: controller.signal
    })
    .then(response => {
      if (!response.ok) throw new Error('Network response was not ok')
      return response.json()
    })
    .then(data => {
      // Only update the display if this is still the current fetch
      if (this.currentFetch === controller) {
        // Cache the results
        if (!this._searchCache) this._searchCache = new Map()
        this._searchCache.set(cacheKey, data)
        // Limit cache size to prevent memory issues
        if (this._searchCache.size > 50) {
          const firstKey = this._searchCache.keys().next().value
          this._searchCache.delete(firstKey)
        }

        this.displayResults(data)
      }
    })
    .catch(error => {
      // Only handle error if it's not an abort error
      if (error.name !== 'AbortError') {
        this.hideLoadingState()
      }
    })
    .finally(() => {
      // Clear the current fetch if it's this one
      if (this.currentFetch === controller) {
        this.currentFetch = null
      }
    })
  }

  showLoadingState() {
    // Show loading in results area - simplified without spinner
    this.resultsTarget.innerHTML = `
      <div class="searcher__loading">
        <p>Searching...</p>
      </div>
    `
    this.resultsTarget.style.display = 'block'
    this.blankTarget.style.display = 'none'
    this.emptyTarget.style.display = 'none'
    this.numResultsTarget.style.display = 'block'
  }

  hideLoadingState() {
    // Show blank state
    this.resultsTarget.style.display = 'none'
    this.blankTarget.style.display = 'flex'
    this.emptyTarget.style.display = 'none'
    this.numResultsTarget.style.display = 'none'
  }

  // Returns HTML-safe markup: the text is HTML-escaped first, then matches are
  // wrapped in <strong>. Server fields (name, company_name, nickname) are not
  // escaped by the JSON API, so escaping here is required to prevent XSS.
  highlightMatch(text, query) {
    const safeText = this.escape(text)
    if (!query || query.length < this.constructor.minSearchChars) {
      return safeText
    }

    const escapedQuery = query.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
    const regex = new RegExp(`(${escapedQuery})`, 'gi')

    return safeText.replace(regex, '<strong class="searcher__highlight">$1</strong>')
  }

  // HTML-escape an arbitrary value so it is safe to interpolate into markup as
  // text or inside a double-quoted attribute.
  escape(value) {
    const div = document.createElement('div')
    div.textContent = String(value ?? '')
    return div.innerHTML
  }

  // Only allow http(s) URLs, fragments, and single-slash same-origin paths.
  // Protocol-relative URLs (//evil.example) are rejected so an attacker-supplied
  // result URL can't redirect off-site, and anything else (javascript:, data:,
  // etc.) is dropped so it can't run when the row is clicked. The result is
  // HTML-escaped for safe attribute interpolation.
  safeUrl(value) {
    const url = String(value ?? '').trim()
    if (/^(https?:\/\/|#|\/(?!\/))/i.test(url)) {
      return this.escape(url)
    }
    return '#'
  }

  iconStyle(result) {
    if (result.object_type === 'Asset' && result.icon_color && result.icon_background_color) {
      return ` style="color: ${this.escape(result.icon_color)}; background-color: ${this.escape(result.icon_background_color)};"`
    }
    return ''
  }

  // Builds one result row. Every server-provided field is escaped (text via
  // escape/highlightMatch, URLs via safeUrl) before it reaches innerHTML.
  buildResultRow(result, type, isSelected, showNickname = true) {
    const query = this.inputTarget.value
    const selectedClass = isSelected ? ' searcher__result--selected' : ''

    const iconMarkup = result.object_type === 'Company' && result.logo
      ? `<div class="searcher__result-logo-container">
          <img src="${this.safeUrl(result.logo)}" class="searcher__result-logo" alt="${this.escape(result.name)} logo" onerror="this.style.display='none'; this.nextElementSibling.style.display='flex';"/>
          <i class="searcher__result-icon fas fa-store-alt" style="display: none;"></i>
         </div>`
      : `<i class='searcher__result-icon ${this.escape(result.icon || type.icon)}'${this.iconStyle(result)}></i>`

    const archivedBadge = result.archived ? '<span class="badge badge--archived">Archived</span>' : ''
    const companyLine = result.company_name ? `<p>${this.highlightMatch(result.company_name, query)}</p>` : ''
    const nicknameLine = showNickname && result.object_type === 'Company' && result.nickname
      ? `<p>${this.highlightMatch(result.nickname, query)}</p>`
      : ''

    return `
      <a href="${this.safeUrl(result.url)}" class='searcher__result${selectedClass}' tabindex="-1" target="_top"
          data-action="mouseenter->search#mouseEnter mouseleave->search#mouseLeave">
        <main>
          ${iconMarkup}
          <div class="searcher__result_content">
            <h2>${this.highlightMatch(result.name, query)}${archivedBadge}</h2>
            ${companyLine}
            ${nicknameLine}
          </div>
        </main>
        <span>${this.printType(result)}</span>
      </a>
    `
  }

  // Calculate ranking score for a result based on highlighted text
  calculateRankingScore(result, query) {
    if (!query || query.length < this.constructor.minSearchChars) {
      return 0
    }

    let score = 0
    const escapedQuery = query.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
    const regex = new RegExp(`(${escapedQuery})`, 'gi')

    // Check name field
    if (result.name) {
      const nameMatches = result.name.match(regex)
      if (nameMatches) {
        // Add points for having matches in name (higher priority)
        score += 100
        // Add points for each character matched in name
        score += nameMatches.reduce((sum, match) => sum + match.length, 0) * 10
      }
    }

    // Check company_name field
    if (result.company_name) {
      const companyMatches = result.company_name.match(regex)
      if (companyMatches) {
        // Add points for having matches in company name (lower priority than name)
        score += 50
        // Add points for each character matched in company name
        score += companyMatches.reduce((sum, match) => sum + match.length, 0) * 5
      }
    }

    return score
  }

  // Sort results by ranking score (highest first)
  sortResultsByRanking(results, query) {
    return results.sort((a, b) => {
      const scoreA = this.calculateRankingScore(a, query)
      const scoreB = this.calculateRankingScore(b, query)
      return scoreB - scoreA // Descending order
    })
  }

  displayResults(data) {
    this._lastSearchData = data
    this.updateAdvancedSearchLink()

    // Set initial display states
    this.resultsTarget.style.display = 'block'
    this.blankTarget.style.display = 'none'
    this.emptyTarget.style.display = 'none'
    this.numResultsTarget.style.display = 'block'

    // Calculate total results
    let totalResults = 0
    if (Array.isArray(data)) {
      totalResults = data.length
    } else {
      totalResults = Object.values(data).reduce((sum, arr) => sum + (arr.results?.length || 0), 0)
    }
    this.numResultsTarget.textContent = `${totalResults} result${totalResults === 1 ? '' : 's'}`

    // Set initial selection to first result
    this.selectedIndex = 0

    const query = this.inputTarget.value
    const fragment = document.createDocumentFragment()

    // Define the order and display names for each type
    const types = [
      { key: 'companies', name: this.structureNameValue, icon: 'far fa-store-alt' },
      { key: 'assets', name: 'Assets', icon: 'far fa-circle' },
      { key: 'passwords', name: 'Passwords', icon: 'far fa-key' },
      { key: 'articles', name: 'Articles', icon: 'far fa-book-reader' },
      { key: 'processes', name: 'Processes', icon: 'far fa-check' },
      { key: 'websites', name: 'Websites', icon: 'far fa-globe' }
    ]

    // Filter out the companies section when scoped to a single company, since a
    // company can't be filtered by itself. The exception is an explicit
    // Companies scope: there the company filter is ignored (mirroring the fetch
    // in executeSearch), so keep companies in or the specific-view lookup below
    // finds nothing and the panel stays stuck on the loading state.
    const filteredTypes = this.onlyCompany && this.specificViewType !== 'companies'
      ? types.filter(type => type.key !== 'companies')
      : types

    // Handle specific view mode
    if (this.specificViewMode) {
      // Convert singular type to plural for display
      const pluralType = this.getPluralType(this.specificViewType)
      const type = filteredTypes.find(t => t.key === pluralType)

      if (type) {
        let typeResults = []
        if (Array.isArray(data)) {
          typeResults = data
        } else if (data[type.key]?.results) {
          typeResults = data[type.key].results
        } else if (data.results) {
          typeResults = data.results
        }

        if (typeResults.length > 0) {
          // Sort results by ranking score within this section
          const sortedResults = this.sortResultsByRanking(typeResults, query)

          const resultsDiv = document.createElement('div')
          resultsDiv.className = 'searcher__type-results'
          resultsDiv.innerHTML = sortedResults
            .map((result, index) => this.buildResultRow(result, type, index === 0, false))
            .join('')

          this.resultsTarget.innerHTML = ''
          this.resultsTarget.appendChild(resultsDiv)
          this.resultsTarget.style.display = 'block'
          this.blankTarget.style.display = 'none'
          this.emptyTarget.style.display = 'none'
          this.numResultsTarget.style.display = 'block'
        } else {
          this.resultsTarget.style.display = 'none'
          this.blankTarget.style.display = 'none'
          this.emptyTarget.style.display = 'flex'
          this.numResultsTarget.style.display = 'none'
        }
      }
    }
    // Handle single view mode
    else if (this.currentViewMode === 'single') {
      const type = filteredTypes.find(t => t.key === this.currentViewType)

      if (type) {  // Only proceed if type exists
        // Handle both array and object data structures
        let typeResults = []
        if (Array.isArray(data)) {
          typeResults = data
        } else if (data[type.key]?.results) {
          typeResults = data[type.key].results
        } else if (data.results) {
          typeResults = data.results
        }

        if (typeResults.length > 0) {  // Only show section if there are results
          // Sort results by ranking score within this section
          const sortedResults = this.sortResultsByRanking(typeResults, query)

          const resultsDiv = document.createElement('div')
          resultsDiv.className = 'searcher__type-results'
          resultsDiv.innerHTML = sortedResults
            .map((result, index) => this.buildResultRow(result, type, index === 0))
            .join('')

          // Update the DOM for single view mode
          this.resultsTarget.innerHTML = ''
          this.resultsTarget.appendChild(resultsDiv)
          this.resultsTarget.style.display = 'block'
          this.blankTarget.style.display = 'none'
          this.emptyTarget.style.display = 'none'
          this.numResultsTarget.style.display = 'block'
        } else {
          // Show empty state if no results for this type
          this.resultsTarget.style.display = 'none'
          this.blankTarget.style.display = 'none'
          this.emptyTarget.style.display = 'flex'
          this.numResultsTarget.style.display = 'none'
        }
      } else {
        // Show empty state if type not found
        this.resultsTarget.style.display = 'none'
        this.blankTarget.style.display = 'none'
        this.emptyTarget.style.display = 'flex'
        this.numResultsTarget.style.display = 'none'
      }
    } else {
      let hasAnyResults = false
      let isFirstResult = true

      filteredTypes.forEach(type => {
        const typeResults = data[type.key]?.results || []

        if (typeResults.length > 0) {  // Only show section if there are results
          // Sort results by ranking score within this section
          const sortedResults = this.sortResultsByRanking(typeResults, query)

          hasAnyResults = true
          const section = document.createElement('div')
          section.className = 'searcher__type-section'
          const rowsHtml = sortedResults.slice(0, 5).map((result, index) => {
            const isSelected = isFirstResult && index === 0
            if (isSelected) isFirstResult = false
            return this.buildResultRow(result, type, isSelected)
          }).join('')

          const viewAllLabel = type.key === 'companies'
            ? this.structurePluralValue.toLowerCase()
            : type.name.toLowerCase()

          section.innerHTML = `
            <div class="searcher__type-header">
              <i class="${this.escape(type.icon)}"></i>
              <h3>${this.escape(type.name)} (${typeResults.length})</h3>
            </div>
            <div class="searcher__type-results">
              ${rowsHtml}
              ${typeResults.length > 5 ? `
                <div class="searcher__more-results">
                  <a href="#" class="searcher__more-link" data-action="click->search#viewAllResults" data-type="${this.escape(type.key)}" tabindex="0">
                    View all ${typeResults.length} ${this.escape(viewAllLabel)}
                  </a>
                </div>
              ` : ''}
            </div>
          `
          fragment.appendChild(section)
        }
      })

      // If no sections have results, show the empty state
      if (!hasAnyResults) {
        this.resultsTarget.style.display = 'none'
        this.blankTarget.style.display = 'none'
        this.emptyTarget.style.display = 'flex'
        this.numResultsTarget.style.display = 'none'
      } else {
        // Single DOM update
        this.resultsTarget.innerHTML = ''
        this.resultsTarget.appendChild(fragment)
        this.resultsTarget.style.display = 'block'
        this.blankTarget.style.display = 'none'
        this.emptyTarget.style.display = 'none'
      }
    }

    // Refocus the input and rebind keyboard events
    this.inputTarget.focus()
    this.bindKeyboardEvents()
  }

  printType(result) {
    switch(result.object_type) {
      case 'Asset':
        return this.escape(result.asset_type)
      default:
        return ""
    }
  }

  mouseEnter(event) {
    // Don't add selected class on hover, just let CSS :hover handle it
    const results = Array.from(this.resultsTarget.querySelectorAll('.searcher__result'))
    this.selectedIndex = results.indexOf(event.currentTarget)
  }

  mouseLeave(event) {
    // Don't remove selected class on mouse leave, only on keyboard navigation
    this.selectedIndex = -1
  }

  // Restores the panel to its just-opened state; the modal got this for free by
  // being torn down on close.
  reset() {
    clearTimeout(this.debounceTimer)
    if (this.currentFetch) {
      this.currentFetch.abort()
      this.currentFetch = null
    }

    this.inputTarget.value = ''
    this.selectedIndex = -1
    this.currentViewMode = 'all'
    this.currentViewType = null
    if (this.hasBackButtonTarget) this.backButtonTarget.style.display = 'none'
    this.resetScope()
    this.resetChips()
    this._lastSearchData = null
    this.toggleBlankState(true)
    this.updateAdvancedSearchLink()
  }

  resetChips() {
    this.element.querySelectorAll('.chip--selectable').forEach(chip => {
      if (chip.dataset.companyId) {
        chip.classList.toggle('active', this.onlyCompanyDefault)
      } else if (chip.dataset.searchComprehensiveDefaultValue) {
        chip.classList.toggle('active', chip.dataset.searchComprehensiveDefaultValue === 'true')
      } else {
        chip.classList.remove('active')
      }
    })

    const comprehensiveChip = this.element.querySelector('.chip--selectable[data-search-comprehensive-default-value]')
    this.searchByAll = comprehensiveChip?.dataset.searchComprehensiveDefaultValue === 'true'
    this.searchByExact = false
    this.onlyMuseum = false
    this.onlyCompany = this.onlyCompanyDefault
  }
  disconnect() {
    // Abort any pending fetch when disconnecting
    if (this.currentFetch) {
      this.currentFetch.abort()
    }

    // Clear any pending debounce timers
    if (this.debounceTimer) {
      clearTimeout(this.debounceTimer)
      this.debounceTimer = null
    }

    // Remove event listeners
    this.element.removeEventListener('keydown', this.handleKeydown)
  }

  // Points the "Advanced search" link at /advanced_search, carrying over the
  // current query, the active/most-relevant type, and the active filters so the
  // full page opens with the same context as the quick-search panel.
  updateAdvancedSearchLink() {
    if (!this.hasAdvancedSearchLinkTarget) return

    const query = this.inputTarget.value?.trim()
    const params = new URLSearchParams()
    if (query) params.set("q", query)

    if (this.currentViewMode === "single" && this.currentViewType) {
      const singularType = this.getSingularType(this.currentViewType)
      if (singularType) params.set("type", singularType)
    } else if (this.specificViewMode && this.specificViewType) {
      const singularType = this.getSingularType(this.specificViewType)
      if (singularType) params.set("type", singularType)
    } else if (this._lastSearchData && !Array.isArray(this._lastSearchData)) {
      const typeOrder = ["companies", "assets", "passwords", "articles", "processes", "websites"]
      const firstWithResults = typeOrder.find((key) => this._lastSearchData[key]?.results?.length > 0)
      if (firstWithResults) {
        params.set("type", this.getSingularType(firstWithResults))
      }
    }

    if (this.searchByAll) params.set("comprehensive", "true")
    if (this.onlyMuseum) params.set("include_archived", "true")
    if (this.onlyCompany && this.companyId) params.set("company", this.companyId)

    const backUrl = this.captureBackUrl()
    if (backUrl) params.set("back_url", backUrl)

    const base = this.advancedSearchLinkTarget.href.split("?")[0]
    this.advancedSearchLinkTarget.href = `${base}${params.toString() ? `?${params}` : ""}`
  }

  captureBackUrl() {
    return window.location.href
  }

  navigateToAdvancedSearch() {
    if (!this.hasAdvancedSearchLinkTarget) return

    this.updateAdvancedSearchLink()
    window.location.href = this.advancedSearchLinkTarget.href
  }

  // Convert plural type to singular for API
  getSingularType(pluralType) {
    const typeMap = {
      'companies': 'Company',
      'assets': 'Asset',
      'passwords': 'Password',
      'articles': 'Article',
      'websites': 'Website',
      'processes': 'Process'
    }
    return typeMap[pluralType] || 'Asset'
  }

  // Convert singular type to plural for display; plural keys pass through
  getPluralType(singularType) {
    const typeMap = {
      'Company': 'companies',
      'Asset': 'assets',
      'Password': 'passwords',
      'Article': 'articles',
      'Website': 'websites',
      'Process': 'processes'
    }
    return typeMap[singularType] || singularType
  }

  viewAllResults(event) {
    event.preventDefault()
    const type = event.currentTarget.dataset.type
    this.currentViewMode = 'single'
    this.currentViewType = type
    this.backButtonTarget.style.display = 'flex'
    this.search()
  }

  backToAllResults() {
    this.currentViewMode = 'all'
    this.currentViewType = null
    this.backButtonTarget.style.display = 'none'
    this.search()
  }
}
